import { useState, useRef, useEffect } from 'react';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { supabase } from '../lib/supabase';

const IS_LOCAL = import.meta.env.VITE_ENV === 'LOCAL';

const DEV_USERS = [
  {
    email: 'developer@rurai.dev',
    password: 'dev-local-password',
  },
  {
    email: 'other_developer@rurai.dev',
    password: 'dev-local-password',
  },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localMagicLink, setLocalMagicLink] = useState<
    string | null
  >(null);

  const joinToken = searchParams.get('join_token');
  const calendarIdFromPath = (() => {
    const m = window.location.pathname.match(
      /^\/calendar\/([^/?#]+)/
    );
    return m ? m[1] : null;
  })();
  const isJoinFlow = Boolean(
    joinToken && calendarIdFromPath
  );

  const [joinCalendar, setJoinCalendar] = useState<{
    name: string;
    color: string;
  } | null>(null);

  useEffect(() => {
    if (!joinToken || !calendarIdFromPath) return;
    fetch(
      `/api/calendars/${encodeURIComponent(calendarIdFromPath)}?join_token=${encodeURIComponent(joinToken)}`
    )
      .then(r => r.json())
      .then(json => {
        if (json.success && json.data?.calendar)
          setJoinCalendar(json.data.calendar);
      })
      .catch(() => {});
  }, [joinToken, calendarIdFromPath]);

  const [selectedDevEmail, setSelectedDevEmail] = useState(
    DEV_USERS[0].email
  );
  const submitting = useRef(false);

  async function handleDevLogin() {
    const devUser = DEV_USERS.find(
      u => u.email === selectedDevEmail
    );
    if (!devUser) return;

    setLoading(true);
    setError(null);
    const { error } =
      await supabase.auth.signInWithPassword({
        email: devUser.email,
        password: devUser.password,
      });
    setLoading(false);
    if (error) {
      setError(error.message);
    } else {
      navigate('/calendar', { replace: true });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setLoading(true);
    setError(null);
    setLocalMagicLink(null);
    const nextPath =
      window.location.pathname + window.location.search;
    const redirectTo =
      `${window.location.origin}/auth/confirm` +
      (nextPath !== '/' && nextPath !== ''
        ? `?next=${encodeURIComponent(nextPath)}`
        : '');
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo },
    });
    submitting.current = false;
    setLoading(false);
    if (error) {
      setError(error.message);
      return;
    }
    setSent(true);
    if (IS_LOCAL) {
      try {
        const res = await fetch('/api/e2e/magic-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            redirectTo: nextPath,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.confirmationUrl)
            setLocalMagicLink(data.confirmationUrl);
        }
      } catch {
        // non-fatal — dev can still use Mailpit
      }
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-paper-200 px-4">
      <div className="paper-card w-full max-w-sm p-8">
        <h1 className="font-serif text-2xl font-bold text-ink-900 mb-1">
          CommonCal
        </h1>
        <p className="font-mono text-sm text-ink-400 mb-7">
          shared group calendar
        </p>

        {isJoinFlow && (
          <div className="mb-6 px-4 py-3 bg-teal-50 border border-teal-200 rounded-sm space-y-1.5">
            {joinCalendar ? (
              <>
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-sm shrink-0"
                    style={{
                      backgroundColor: joinCalendar.color,
                    }}
                  />
                  <span className="font-mono text-xs font-semibold text-teal-900">
                    {joinCalendar.name}
                  </span>
                </div>
                <p className="font-mono text-xs text-teal-800 leading-relaxed">
                  Sign in to join this shared calendar.
                  After signing in you'll be subscribed
                  automatically.
                </p>
              </>
            ) : (
              <p className="font-mono text-xs text-teal-800 leading-relaxed">
                <span className="font-semibold">
                  You've been invited to join a shared
                  calendar.
                </span>{' '}
                Sign in to accept the invitation.
              </p>
            )}
          </div>
        )}

        {sent ? (
          <div className="space-y-3">
            {IS_LOCAL && localMagicLink ? (
              <>
                <p className="font-mono text-xs text-ink-500">
                  Local dev — click to sign in as{' '}
                  <span className="font-semibold">
                    {email}
                  </span>
                  :
                </p>
                <a
                  href={localMagicLink}
                  className="btn-primary w-full block text-center text-xs"
                >
                  Click magic link
                </a>
              </>
            ) : IS_LOCAL ? (
              <p className="font-mono text-xs text-ink-500">
                Generating local link for{' '}
                <span className="font-semibold">
                  {email}
                </span>
                …
              </p>
            ) : (
              <p className="font-mono text-sm text-ink-700">
                Check your email — a magic link is on its
                way to{' '}
                <span className="font-semibold">
                  {email}
                </span>
                .
              </p>
            )}
            <button
              onClick={() => {
                setSent(false);
                setLocalMagicLink(null);
              }}
              className="btn-ghost text-xs"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div>
              <label
                htmlFor="email"
                className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoFocus
                className="input-field"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            {error && (
              <p className="font-mono text-xs text-red-600">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || !email}
              className="btn-primary w-full"
            >
              {loading ? 'Sending…' : 'Send magic link'}
            </button>
          </form>
        )}

        {IS_LOCAL && (
          <div className="mt-6 pt-5 border-t border-paper-300 space-y-2">
            <p className="font-mono text-xs text-ink-300 uppercase tracking-widest">
              Local dev
            </p>
            <select
              value={selectedDevEmail}
              onChange={e =>
                setSelectedDevEmail(e.target.value)
              }
              className="input-field w-full text-xs font-mono"
              aria-label="Select local developer account"
            >
              {DEV_USERS.map(devUser => (
                <option
                  key={devUser.email}
                  value={devUser.email}
                >
                  {devUser.email}
                </option>
              ))}
            </select>
            <button
              onClick={handleDevLogin}
              disabled={loading}
              className="btn-ghost w-full text-xs text-ink-400"
            >
              Sign in as {selectedDevEmail}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
